import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
    ArrowRight,
    MessageSquareText,
    Zap,
    Landmark,
    Shield,
    HeartHandshake,
    Compass,
    Smartphone,
    Sparkles,
    Users,
    Quote,
    CheckCircle2,
    Gift,
    HandHeart,
    Search,
    FileText,
    BadgeCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import API from "@/lib/api";
import { ShareButton, appSharePayload } from "@/components/share/ShareButton";
import InstallHomeBanner from "@/components/pwa/InstallHomeBanner";
import InstallHomeCardIOS from "@/components/pwa/InstallHomeCardIOS";

const TRUST_BADGES = [
    "Servizio 100% gratuito",
    "Professionisti reali",
    "Nessuna AI",
    "Zero pubblicità",
];

const VALUE_ITEMS = [
    { icon: Compass, title: "Orientarsi con facilità", text: "Informazioni chiare per comprendere meglio il mondo del condominio." },
    { icon: HandHeart, title: "Comprendere prima di decidere", text: "Ti aiutiamo a conoscere regole, diritti e doveri in modo semplice e accessibile." },
    { icon: HeartHandshake, title: "Favorire una convivenza migliore", text: "Più consapevolezza significa meno incomprensioni e rapporti più sereni." },
    { icon: Smartphone, title: "Il condominio a portata di mano", text: "Professionisti del settore e contenuti utili sempre disponibili quando ne hai bisogno." },
];

// Custom counter — animates 0 → end when the number first appears on screen.
// Respects prefers-reduced-motion.
function useCountUp(end, duration = 900) {
    const [n, setN] = useState(end ?? 0);
    useEffect(() => {
        if (end == null) return;
        if (typeof window !== "undefined" &&
            window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            setN(end);
            return;
        }
        let raf = 0; let start = null;
        const step = (t) => {
            if (!start) start = t;
            const p = Math.min(1, (t - start) / duration);
            const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
            setN(Math.floor(eased * end));
            if (p < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [end, duration]);
    return n;
}

function useFaqCount() {
    const [count, setCount] = useState(null);
    useEffect(() => {
        API.get("/public/faqs")
            .then(({ data }) => setCount(Array.isArray(data) ? data.length : null))
            .catch(() => setCount(null));
    }, []);
    return count;
}

function Hero({ isLoggedIn, faqCount }) {
    const animatedFaq = useCountUp(faqCount);
    return (
        <section className="relative overflow-hidden bg-white">
            <div
                aria-hidden
                className="absolute inset-0 bg-[radial-gradient(1200px_500px_at_10%_-10%,rgba(48,131,201,0.10),transparent_65%),radial-gradient(900px_400px_at_100%_10%,rgba(245,149,71,0.12),transparent_60%)]"
            />
            {/* subtle grid texture */}
            <div
                aria-hidden
                className="absolute inset-0 opacity-[0.025] [background-image:linear-gradient(rgba(48,131,201,1)_1px,transparent_1px),linear-gradient(90deg,rgba(48,131,201,1)_1px,transparent_1px)] [background-size:44px_44px]"
            />
            <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14 sm:py-20 lg:py-24">
                <div className="grid gap-10 lg:grid-cols-[1.05fr_1fr] lg:gap-14 items-center">
                    {/* LEFT — copy */}
                    <div>
                        <div
                            className="inline-flex items-center gap-2 rounded-full bg-primary/10 border border-primary/20 px-3 py-1.5 text-xs font-semibold uppercase tracking-widest text-primary"
                            data-testid="hero-overline"
                        >
                            <Sparkles className="size-3.5" /> L'esperto risponde
                        </div>
                        <h1
                            data-testid="hero-title"
                            className="mt-5 font-serif text-4xl sm:text-5xl lg:text-[64px] font-semibold leading-[1.05] tracking-tight text-slate-900"
                        >
                            Il condominio
                            <br />
                            <span className="relative inline-block text-primary">
                                a portata di mano.
                            </span>
                        </h1>
                        <p className="mt-6 text-lg sm:text-xl text-slate-600 leading-relaxed max-w-2xl">
                            Dal dubbio quotidiano alle questioni più complesse, CondominioNow ti aiuta a trovare la risposta giusta grazie a{" "}
                            <strong className="text-slate-900">Due strumenti pensati per aiutarti a conoscere, comprendere e affrontare le principali problematiche condominiali.</strong>
                        </p>

                        <div
                            className="mt-8 flex flex-wrap gap-2"
                            data-testid="hero-trust-badges"
                        >
                            {TRUST_BADGES.map((b) => (
                                <span
                                    key={b}
                                    className="inline-flex items-center gap-1.5 rounded-full bg-primary/[0.08] border border-primary/15 px-3 py-1.5 text-[13px] font-medium text-slate-800"
                                >
                                    <CheckCircle2 className="size-3.5 text-primary" />
                                    {b}
                                </span>
                            ))}
                        </div>

                        <div
                            className="mt-8 flex flex-col sm:flex-row gap-3"
                            data-testid="hero-cta-row"
                        >
                            <Button
                                asChild
                                size="lg"
                                className="bg-accent hover:bg-[#E17F2C] text-white font-semibold rounded-full h-12 px-6 shadow-[0_6px_16px_-8px_rgba(245,149,71,0.6)] transition"
                                data-testid="hero-cta-ask"
                            >
                                <a href="#invia-domanda">
                                    Invia una domanda
                                    <ArrowRight className="size-4 ml-1" />
                                </a>
                            </Button>
                            <Button
                                asChild
                                size="lg"
                                className="bg-primary hover:bg-[#2570B0] text-white font-semibold rounded-full h-12 px-6 shadow-[0_6px_16px_-8px_rgba(48,131,201,0.6)] transition"
                                data-testid="hero-cta-faq"
                            >
                                <a href="#faq-veloci">
                                    Trova una risposta in pochi secondi
                                    <ArrowRight className="size-4 ml-1" />
                                </a>
                            </Button>
                        </div>
                    </div>

                    {/* RIGHT — visual composition */}
                    <div
                        className="relative hidden lg:block h-[440px]"
                        aria-hidden
                        data-testid="hero-visual"
                    >
                        {/* blobs */}
                        <div className="absolute -top-10 -right-6 size-64 rounded-full bg-primary/15 blur-3xl" />
                        <div className="absolute bottom-2 -left-8 size-56 rounded-full bg-accent/25 blur-3xl" />

                        {/* Card A — user question */}
                        <div className="cn-float-a absolute top-4 left-2 w-[280px] rounded-2xl border border-slate-200 bg-white shadow-[0_20px_50px_-25px_rgba(15,23,42,0.25)] p-5">
                            <div className="flex items-center gap-2 text-[10.5px] font-semibold uppercase tracking-widest text-primary">
                                <FileText className="size-3.5" /> La tua domanda
                            </div>
                            <p className="mt-3 font-serif text-[15px] leading-snug text-slate-900">
                                "Come posso contestare il rendiconto approvato dall'assemblea?"
                            </p>
                            <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
                                <div className="size-6 rounded-full bg-slate-100 flex items-center justify-center font-semibold text-slate-500">
                                    C
                                </div>
                                Condomino · 2 min fa
                            </div>
                        </div>

                        {/* Card B — expert answer */}
                        <div className="cn-float-b absolute top-[125px] right-0 w-[290px] rounded-2xl border border-slate-200 bg-white shadow-[0_20px_50px_-25px_rgba(15,23,42,0.25)] p-5">
                            <div className="flex items-center gap-2 text-[10.5px] font-semibold uppercase tracking-widest text-accent">
                                <BadgeCheck className="size-3.5" /> Risposta del professionista
                            </div>
                            <p className="mt-3 text-[14px] leading-relaxed text-slate-700">
                                Il condomino può impugnare il rendiconto entro 30 giorni dalla comunicazione ai sensi dell'art. 1137 c.c.
                            </p>
                            <div className="mt-4 flex items-center gap-2">
                                <div className="size-7 rounded-full bg-primary text-white flex items-center justify-center text-[11px] font-semibold">
                                    LC
                                </div>
                                <div className="text-xs">
                                    <div className="font-semibold text-slate-900">Dr. L. Colaianni</div>
                                    <div className="text-slate-500">Fondatore CondominioNow</div>
                                </div>
                            </div>
                        </div>

                        {/* Card C — FAQ hero message (no counter, no numbers) */}
                        <div className="cn-float-c absolute bottom-0 left-8 w-[280px] rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/[0.06] via-white to-white shadow-[0_20px_50px_-25px_rgba(48,131,201,0.35)] p-5">
                            <div className="flex items-center gap-2 text-[10.5px] font-semibold uppercase tracking-widest text-primary">
                                <span className="size-1.5 rounded-full bg-primary cn-pulse-dot" />
                                Domande e Risposte Veloci
                            </div>
                            <p className="mt-3 font-serif text-[17px] leading-snug font-semibold text-slate-900">
                                Le risposte ai dubbi più comuni del condominio sono già qui.
                            </p>
                            <p className="mt-2 text-[13.5px] text-slate-700 leading-relaxed">
                                Scrivi il tuo dubbio con parole tue.<br />
                                Potresti trovare la risposta che cerchi in pochi secondi.
                            </p>
                            <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary">
                                <Search className="size-3.5" /> Ricerca in linguaggio naturale
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function ToolsSection({ isLoggedIn, faqCount }) {
    const animated = useCountUp(faqCount);
    const askHref = isLoggedIn ? "/dashboard/nuova" : "/registrati";
    return (
        <section className="bg-white">
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pb-16 lg:pb-24 pt-2 lg:pt-6">
                <div className="max-w-2xl mb-10 lg:mb-14">
                    <div className="text-xs uppercase tracking-widest text-primary font-semibold inline-flex items-center gap-1.5">
                        <Zap className="size-3.5" /> Scegli come iniziare
                    </div>
                    <h2
                        data-testid="tools-title"
                        className="mt-2 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight text-slate-900"
                    >
                        Due strumenti, <span className="text-primary">un solo obiettivo</span>.
                    </h2>
                    <p className="mt-3 text-slate-600 text-lg leading-relaxed">
                        Trova la risposta giusta al tuo dubbio condominiale.
                    </p>
                </div>

                <div className="grid gap-6 lg:gap-8 md:grid-cols-2 items-stretch" data-testid="tools-grid">
                    {/* TOOL 1 — Ask a question (arancio) */}
                    <div
                        id="invia-domanda"
                        data-testid="tool-ask"
                        className="group relative flex flex-col scroll-mt-24 rounded-2xl border border-accent/25 bg-gradient-to-br from-accent/[0.06] via-white to-white p-5 sm:p-6 lg:p-8 shadow-[0_1px_3px_rgba(15,23,42,0.04),0_4px_12px_rgba(15,23,42,0.03)] transition duration-200 hover:shadow-[0_10px_30px_-15px_rgba(245,149,71,0.45)] hover:-translate-y-0.5"
                    >
                        <div className="flex items-start justify-between">
                            <div className="size-14 rounded-2xl bg-accent text-white flex items-center justify-center shadow-[0_10px_20px_-10px_rgba(245,149,71,0.7)]">
                                <MessageSquareText className="size-7" />
                            </div>
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/15 border border-accent/25 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-orange-800">
                                Il tuo caso
                            </span>
                        </div>

                        <h3 className="mt-6 font-serif text-2xl lg:text-3xl font-semibold text-slate-900 leading-tight">
                            Fai una domanda
                        </h3>
                        <p className="mt-2 text-sm font-medium text-slate-500 italic">
                            Ho un problema specifico
                        </p>
                        <p className="mt-4 text-slate-600 leading-relaxed">
                            Descrivi la tua situazione. Un professionista reale ti risponde direttamente e in modo privato.
                        </p>

                        <ul className="mt-6 space-y-2.5 text-[14.5px] text-slate-700">
                            <li className="flex items-start gap-2">
                                <CheckCircle2 className="size-5 text-accent shrink-0 mt-0.5" />
                                Risposta in 24-48 ore
                            </li>
                            <li className="flex items-start gap-2">
                                <CheckCircle2 className="size-5 text-accent shrink-0 mt-0.5" />
                                Privata e riservata
                            </li>
                            <li className="flex items-start gap-2">
                                <CheckCircle2 className="size-5 text-accent shrink-0 mt-0.5" />
                                Da esperti reali del settore
                            </li>
                        </ul>

                        <div className="mt-8 pt-2 flex-1 flex items-end">
                            <Button
                                asChild
                                size="lg"
                                className="w-full min-w-0 whitespace-normal leading-tight text-center bg-accent hover:bg-[#E17F2C] text-white font-semibold rounded-full min-h-12 py-2 px-4 sm:px-6 shadow-[0_6px_16px_-8px_rgba(245,149,71,0.6)] transition"
                                data-testid="tool-ask-cta"
                            >
                                <Link to={askHref}>
                                    Invia una domanda
                                    <ArrowRight className="size-4 ml-1 transition-transform group-hover:translate-x-0.5" />
                                </Link>
                            </Button>
                        </div>
                    </div>

                    {/* TOOL 2 — Quick FAQ library (blu) */}
                    <div
                        id="faq-veloci"
                        data-testid="tool-faq"
                        className="group relative flex flex-col scroll-mt-24 rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/[0.06] via-white to-white p-5 sm:p-6 lg:p-8 shadow-[0_1px_3px_rgba(15,23,42,0.04),0_4px_12px_rgba(15,23,42,0.03)] transition duration-200 hover:shadow-[0_10px_30px_-15px_rgba(48,131,201,0.45)] hover:-translate-y-0.5"
                    >
                        <div className="flex items-start justify-between">
                            <div className="size-14 rounded-2xl bg-primary text-white flex items-center justify-center shadow-[0_10px_20px_-10px_rgba(48,131,201,0.7)]">
                                <Search className="size-7" />
                            </div>
                        </div>

                        <h3 className="mt-6 font-serif text-2xl lg:text-3xl font-semibold text-slate-900 leading-tight">
                            Domande e Risposte Veloci
                        </h3>
                        <p className="mt-2 text-sm font-medium text-slate-500 italic">
                            Le risposte ai dubbi più comuni del condominio sono già qui.
                        </p>
                        <p className="mt-4 text-slate-600 leading-relaxed">
                            Scrivi il tuo dubbio con parole tue.<br />
                            Potresti trovare la risposta che cerchi in pochi secondi.
                        </p>

                        <ul className="mt-6 space-y-2.5 text-[14.5px] text-slate-700">
                            <li className="flex items-start gap-2">
                                <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                                Nessuna registrazione richiesta
                            </li>
                            <li className="flex items-start gap-2">
                                <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                                Ricerca in linguaggio naturale
                            </li>
                            <li className="flex items-start gap-2">
                                <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                                Sempre aggiornate dai professionisti
                            </li>
                        </ul>

                        <div className="mt-8 pt-2 flex-1 flex items-end">
                            <Button
                                asChild
                                size="lg"
                                className="w-full min-w-0 whitespace-normal leading-tight text-center bg-primary hover:bg-[#2570B0] text-white font-semibold rounded-full min-h-12 py-2 px-4 sm:px-6 shadow-[0_6px_16px_-8px_rgba(48,131,201,0.6)] transition"
                                data-testid="tool-faq-cta"
                            >
                                <Link to="/faq">
                                    Trova una risposta in pochi secondi
                                    <ArrowRight className="size-4 ml-1 transition-transform group-hover:translate-x-0.5" />
                                </Link>
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function ValueSection() {
    return (
        <section className="bg-primary text-white relative overflow-hidden">
            <div
                aria-hidden
                className="absolute inset-0 bg-[radial-gradient(700px_260px_at_0%_0%,rgba(245,149,71,0.30),transparent_60%),radial-gradient(600px_260px_at_100%_100%,rgba(255,255,255,0.10),transparent_60%)]"
            />
            <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
                <div className="grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:gap-16 items-start">
                    <div>
                        <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-accent">
                            <HeartHandshake className="size-3.5" /> Utilità sociale
                        </div>
                        <h2
                            data-testid="value-title"
                            className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight"
                        >
                            Più informazione,
                            <br />
                            <span className="text-accent">meno conflitti.</span>
                        </h2>
                        <p className="mt-5 text-white/85 text-lg leading-relaxed">
                            CondominioNow è un progetto gratuito di utilità sociale. Crediamo che una persona informata sia una persona più serena — e che la comprensione delle regole sia il primo passo verso una convivenza rispettosa.
                        </p>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2" data-testid="value-cards">
                        {VALUE_ITEMS.map((v) => {
                            const Icon = v.icon;
                            return (
                                <div
                                    key={v.title}
                                    className="rounded-2xl bg-white/10 border border-white/15 p-5 hover:bg-white/[0.14] transition backdrop-blur-sm"
                                >
                                    <Icon className="size-5 text-accent" />
                                    <div className="mt-3 font-serif text-lg font-semibold">{v.title}</div>
                                    <p className="mt-1 text-sm text-white/85 leading-relaxed">{v.text}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
}

function StorySection() {
    return (
        <section className="bg-white">
            <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
                <div className="text-xs uppercase tracking-widest text-primary font-semibold">La nostra storia</div>
                <h2
                    data-testid="story-title"
                    className="mt-2 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight text-slate-900"
                >
                    Perché è nato CondominioNow.
                </h2>

                <div className="mt-10 rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-white p-6 sm:p-10 relative overflow-hidden">
                    <Quote className="absolute top-6 right-6 size-16 text-primary/10" aria-hidden />
                    <div className="relative">
                        <p className="font-serif text-xl sm:text-2xl leading-relaxed text-slate-800">
                            "CondominioNow nasce per aiutare i condomini ad orientarsi tra norme, assemblee, spese e decisioni condominiali, offrendo informazioni chiare e facilmente accessibili."
                        </p>
                        <p className="mt-6 font-serif text-lg sm:text-xl leading-relaxed text-slate-700 italic">
                            Perché una persona informata è una persona più serena.
                        </p>

                        <div className="mt-8 flex items-center gap-4 pt-6 border-t border-slate-200">
                            <div className="size-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-serif text-lg font-semibold shrink-0">
                                LC
                            </div>
                            <div>
                                <div className="font-semibold text-slate-900" data-testid="story-signature">Dott. Luigi Colaianni</div>
                                <div className="text-sm text-muted-foreground">Fondatore di CondominioNow</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function TrustSection() {
    const items = [
        {
            icon: Gift,
            title: "Sempre gratis",
            text: "Nessun costo, nessun abbonamento nascosto, nessuna pubblicità. Un progetto di utilità pubblica.",
        },
        {
            icon: Users,
            title: "Persone, non algoritmi",
            text: "Le risposte vengono scritte dal team di NowGestioni coordinato dal Dr. Luigi Colaianni. Zero AI generativa.",
        },
        {
            icon: Shield,
            title: "Privacy garantita",
            text: "Le tue domande restano riservate. Solo tu e il professionista che le prende in carico possono leggerle.",
        },
    ];
    return (
        <section className="bg-slate-50/60 border-y border-slate-200">
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
                <div className="max-w-2xl mb-10">
                    <div className="text-xs uppercase tracking-widest text-primary font-semibold">
                        Perché scegliere CondominioNow
                    </div>
                    <h2 className="mt-2 font-serif text-3xl sm:text-4xl font-semibold leading-tight text-slate-900">
                        Un servizio pensato per i condomini.
                    </h2>
                </div>
                <div className="grid gap-6 md:grid-cols-3" data-testid="trust-grid">
                    {items.map(({ icon: Icon, title, text }) => (
                        <div
                            key={title}
                            className="group rounded-2xl border border-slate-200 bg-white p-6 lg:p-7 shadow-[0_1px_3px_rgba(15,23,42,0.04)] hover:shadow-[0_10px_30px_-15px_rgba(48,131,201,0.30)] hover:-translate-y-0.5 transition duration-200"
                        >
                            <div className="size-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                                <Icon className="size-6" />
                            </div>
                            <h3 className="mt-5 font-serif text-xl font-semibold text-slate-900">{title}</h3>
                            <p className="mt-2 text-slate-600 leading-relaxed">{text}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function FinalCTA({ isLoggedIn, faqCount }) {
    return (
        <section className="bg-primary text-white relative overflow-hidden">
            <div
                aria-hidden
                className="absolute inset-0 bg-[radial-gradient(900px_400px_at_100%_0%,rgba(245,149,71,0.30),transparent_60%),radial-gradient(700px_300px_at_0%_100%,rgba(255,255,255,0.10),transparent_60%)]"
            />
            <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24 text-center">
                <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-accent">
                    <Landmark className="size-3.5" /> Progetto di Nowgestioni S.r.l.
                </div>
                <h2 data-testid="cta-title" className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
                    Qui trovi aiuto, informazioni<br />
                    e orientamento per il tuo condominio.
                </h2>
                <p className="mt-4 text-white/90 text-lg leading-relaxed max-w-2xl mx-auto">
                    Che tu abbia una domanda specifica o voglia solo capirci di più, CondominioNow è dalla tua parte.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center">
                    <Button
                        asChild
                        size="lg"
                        className="bg-accent hover:bg-[#E17F2C] text-white font-semibold rounded-full h-12 px-7 shadow-[0_6px_16px_-8px_rgba(245,149,71,0.7)] transition"
                        data-testid="cta-ask"
                    >
                        <Link to={isLoggedIn ? "/dashboard/nuova" : "/registrati"}>
                            Fai la tua domanda <ArrowRight className="size-4 ml-1" />
                        </Link>
                    </Button>
                    <Button
                        asChild
                        size="lg"
                        variant="outline"
                        className="rounded-full h-12 px-7 border-2 border-white/70 bg-transparent text-white hover:bg-white hover:text-primary transition"
                        data-testid="cta-library"
                    >
                        <Link to="/faq">Sfoglia la biblioteca <ArrowRight className="size-4 ml-1" /></Link>
                    </Button>
                </div>

                {/* Share block — non-promotional peer-help nudge */}
                <div className="mt-12 pt-10 border-t border-white/20">
                    <div className="mx-auto max-w-2xl rounded-3xl bg-white/[0.08] border border-white/25 backdrop-blur-sm px-6 sm:px-10 py-8 sm:py-10 shadow-[0_20px_60px_-25px_rgba(0,0,0,0.4)]">
                        <div className="flex items-center justify-center gap-2 text-accent text-[12px] font-semibold uppercase tracking-[0.18em]">
                            <HandHeart className="size-4" /> Aiuta chi ne ha bisogno
                        </div>
                        <p className="mt-4 text-white text-xl sm:text-2xl leading-snug font-serif font-semibold text-center">
                            Conosci qualcuno con un problema condominiale?
                        </p>
                        <p className="mt-3 text-white/90 text-base sm:text-lg leading-relaxed text-center">
                            Condividi CondominioNow e aiutalo a trovare la risposta che cerca.
                        </p>
                        <div className="mt-7 flex justify-center">
                            <ShareButton
                                payload={appSharePayload()}
                                variant="accent-lg"
                                label="Condividi CondominioNow"
                                data-testid="home-share-app"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default function Home() {
    const { user } = useAuth();
    const isLoggedIn = !!user;
    const faqCount = useFaqCount();

    return (
        <div data-testid="home-page">
            <Hero isLoggedIn={isLoggedIn} faqCount={faqCount} />
            <ToolsSection isLoggedIn={isLoggedIn} faqCount={faqCount} />
            <InstallHomeBanner />
            <InstallHomeCardIOS />
            <TrustSection />
            <FinalCTA isLoggedIn={isLoggedIn} faqCount={faqCount} />
        </div>
    );
}
